import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Lock, Paperclip, FileText, Download, ArrowLeft, Pencil, Trash2, Send } from 'lucide-react';
import { useSelector } from 'react-redux';
import { authSelector } from '../../features/auth/slice';
import axios from 'axios';
import CommentItem from '../../components/board/CommentItem';
import UserAvatar from '../../components/UserAvatar';
import ProfilePopover from '../../components/ProfilePopover';

function ViewPage({ boardType = 'Notice' }) {
  const navigate = useNavigate();
  const { me } = useSelector(authSelector.selectAuthState);
  const { id } = useParams();
  const [article, setArticle] = useState({});
  const [nickName, setNickName] = useState('');
  const [file, setFile] = useState([]);
  const [comments, setComments] = useState([]);
  const [commentCount, setCommentCount] = useState(0);
  const [parentContent, setParentContent] = useState('');
  const [commentUpdate, setCommentUpdate] = useState(true);
  const [commentLoading, setCommentLoading] = useState(false);

  const writer = me?.name;
  const writerNickName = me?.nickName;
  const clientId = me?.id;

  const parentComments = comments
    .filter((comment) => !comment.parent)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  useEffect(() => {
    axios
      .get(`/board/${id}?boardType=${boardType}`, { withCredentials: true })
      .then((response) => {
        setArticle(response.data.article);
        setFile(response.data.file);
        setNickName(response.data.article.client.nickName);
      });
    axios
      .get(`/board/comment/${id}?boardType=${boardType}`, {
        withCredentials: true,
      })
      .then((response) => {
        setComments(response.data);
        const parentOnly = response.data.filter((c) => !c.parent);
        setCommentCount(parentOnly.length);
      });
  }, [id, commentUpdate, boardType]);

  const deleteArticles = () => {
    if (window.confirm('게시물을 삭제하시겠습니까?')) {
      axios
        .delete(`/board/${id}?boardType=${boardType}`, {
          withCredentials: true,
        })
        .then(() => {
          navigate(`/${boardType}/List`);
        });
    }
  };

  const newParentComment = () => {
    if (!writer || !parentContent.trim()) {
      alert('댓글 내용을 입력해 주세요.');
      return;
    }
    setCommentLoading(true);
    const parentComment = {
      boardId: id,
      clientId,
      writer,
      content: parentContent,
      boardType,
    };

    axios
      .post('/board/comment/write', parentComment, {
        withCredentials: true,
      })
      .then((response) => {
        const newComment = response.data;
        const copyComments = [...comments, newComment];
        setComments(copyComments);
        setCommentCount(copyComments.filter((c) => !c.parent).length);
        setParentContent('');
      })
      .catch(() => {
        alert('댓글 등록 중 오류가 발생했습니다.');
      })
      .finally(() => setCommentLoading(false));
  };

  const isOwner = me?.id === article?.clientId || me?.adminYn === 'Y';

  return (
    <>
      {/* 게시글 카드 */}
      <div className="rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
        {/* 헤더 */}
        <div className="px-6 py-5 border-b border-white/[0.08]">
          <div className="flex items-start gap-2 mb-3">
            {article.secretYn === 'Y' && (
              <Lock size={16} className="text-slate-500 mt-0.5 shrink-0" />
            )}
            <h1 className="text-lg font-bold text-white leading-snug">{article.title}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400">
            {article.client ? (
              <ProfilePopover user={{
                userType: 'client',
                userId: article.client.id,
                name: nickName || article.client.name,
                profileImage: article.client.profileImage,
                adminYn: article.client.adminYn,
              }}>
                <UserAvatar
                  name={nickName || article.writer}
                  profileImage={article.client?.profileImage}
                  userType="client"
                  adminYn={article.client?.adminYn}
                  size={22}
                />
                <span>{nickName || article.writer}</span>
              </ProfilePopover>
            ) : (
              <div className="flex items-center gap-1.5">
                <UserAvatar
                  name={nickName || article.writer}
                  profileImage={null}
                  userType="member"
                  adminYn="N"
                  size={22}
                />
                <span>{nickName || article.writer}</span>
              </div>
            )}
            <span>{article.createdAt && new Date(article.createdAt).toLocaleDateString('ko-KR')}</span>
            {article.categoryName && (
              <span
                className={`rounded-full border px-2 py-0.5 font-medium ${
                  article.categoryId === '1'
                    ? 'border-sky-500/20 bg-sky-500/10 text-sky-400'
                    : 'border-pink-500/20 bg-pink-500/10 text-pink-400'
                }`}>
                {article.categoryName}
              </span>
            )}
          </div>
        </div>

        {/* 본문 */}
        <div className="p-6">
          {/* 첨부 파일 */}
          {file?.fileUrl && (
            <div className="mb-5">
              {file.fileUrl.match(/\.(jpeg|jpg|png|gif|bmp|webp)$/i) ? (
                <img
                  src={file.fileUrl}
                  alt="첨부 이미지"
                  className="max-w-full h-auto rounded-xl border border-white/[0.08]"
                />
              ) : file.fileUrl.match(/\.(mp3|wav|ogg)$/i) ? (
                <audio controls className="w-full">
                  <source src={file.fileUrl} />
                </audio>
              ) : (
                <a
                  href={file.fileUrl}
                  download
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.08] text-slate-300 hover:bg-white/[0.06] hover:text-white transition-colors text-sm">
                  <FileText size={16} className="text-slate-400" />
                  <span className="truncate max-w-[200px]">{file.fileUrl.split('/').pop()}</span>
                  <Download size={14} className="text-slate-500 ml-1" />
                </a>
              )}
            </div>
          )}
          <div
            className="board-content prose max-w-none text-sm leading-relaxed bg-white rounded-xl p-5"
            dangerouslySetInnerHTML={{ __html: article.description }}
          />
        </div>
      </div>

      {/* 액션 버튼 */}
      <div className="flex items-center justify-between mt-4">
        <button
          onClick={() => navigate(`/${boardType}/List`)}
          className="flex items-center gap-1.5 px-4 py-2 text-slate-400 hover:text-white text-sm font-medium transition-colors">
          <ArrowLeft size={16} />
          목록
        </button>
        {isOwner && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(`/${boardType}/Modify/${id}`)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-sky-500/20 hover:bg-sky-500/30 text-sky-400 text-sm font-semibold transition-colors">
              <Pencil size={14} />
              수정
            </button>
            <button
              onClick={deleteArticles}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-sm font-semibold bg-rose-500 hover:bg-rose-600 transition-colors">
              <Trash2 size={14} />
              삭제
            </button>
          </div>
        )}
      </div>

      {/* 댓글 섹션 */}
      <div className="mt-6 rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
        <div className="px-6 py-4 bg-white/[0.03] border-b border-white/[0.08] flex items-center gap-2">
          <span className="text-slate-200 text-sm font-semibold">댓글</span>
          <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-400">
            {commentCount}
          </span>
        </div>
        <div className="p-6 space-y-4">
          {/* 댓글 작성 */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <UserAvatar
                name={writerNickName || writer}
                profileImage={me?.profileImage}
                userType={me?.userType}
                adminYn={me?.adminYn}
                size={28}
              />
              <span className="text-sm font-medium text-slate-200">
                {writerNickName || writer}
              </span>
            </div>
            <textarea
              className="w-full h-24 p-3 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-transparent resize-none placeholder-slate-500 transition"
              value={parentContent}
              onChange={(e) => setParentContent(e.target.value)}
              placeholder="댓글을 입력하세요"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  newParentComment();
                }
              }}
            />
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-slate-600">Ctrl+Enter로 등록</span>
              <button
                onClick={newParentComment}
                disabled={commentLoading || !parentContent.trim()}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                  commentLoading || !parentContent.trim()
                    ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                    : 'bg-sky-500 hover:bg-sky-600 text-white'
                }`}>
                <Send size={13} />
                {commentLoading ? '등록 중...' : '등록'}
              </button>
            </div>
          </div>

          {/* 댓글 목록 */}
          {parentComments.length > 0 && (
            <div className="pt-4 border-t border-white/[0.06] space-y-3">
              {parentComments.map((parentComment) => (
                <CommentItem
                  key={parentComment.id}
                  comments={comments}
                  boardId={id}
                  clientId={clientId}
                  boardType={boardType}
                  setComments={setComments}
                  setCommentCount={setCommentCount}
                  me={me}
                  writer={writer}
                  nickName={writerNickName}
                  writerNickName={writerNickName}
                  parentComment={parentComment}
                  commentUpdate={commentUpdate}
                  setCommentUpdate={setCommentUpdate}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default ViewPage;
